import React from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "../components/useDocumentMeta";
import {
  corePrinciples,
  discoverySteps,
  platformLayers,
  rolloutMarkers,
  rolloutPhases,
  rolloutTimelineMonths,
  teamRoles,
} from "../content/offering";
import type { Layer, RolloutPhase } from "../content/offering";

const LAYER_ICONS: Record<Layer["icon"], React.ReactNode> = {
  experience: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </>
  ),
  capability: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </>
  ),
  orchestration: (
    <>
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </>
  ),
  system: (
    <>
      <rect x="2" y="3" width="20" height="8" rx="2" />
      <rect x="2" y="13" width="20" height="8" rx="2" />
      <line x1="6" y1="7" x2="6.01" y2="7" />
      <line x1="6" y1="17" x2="6.01" y2="17" />
    </>
  ),
};

const ROLLOUT_ICONS: Record<RolloutPhase["icon"], React.ReactNode> = {
  // Catalog — an itemised inventory list.
  catalog: (
    <>
      <line x1="9" y1="6" x2="20" y2="6" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <line x1="9" y1="18" x2="20" y2="18" />
      <circle cx="4.5" cy="6" r="1.2" />
      <circle cx="4.5" cy="12" r="1.2" />
      <circle cx="4.5" cy="18" r="1.2" />
    </>
  ),
  // Self-services — speed / one action provisions the rest.
  selfservice: <polygon points="13 2 4 14 12 14 11 22 20 10 12 10 13 2" />,
  // Actionable insights — scorecards and health signals.
  insights: (
    <>
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="6" y="10" width="3" height="11" />
      <rect x="11" y="6" width="3" height="15" />
      <rect x="16" y="13" width="3" height="8" />
    </>
  ),
  // Golden paths — a paved route to a goal.
  goldenpaths: (
    <>
      <line x1="5" y1="22" x2="5" y2="3" />
      <path d="M5 4h12l-2.5 4 2.5 4H5" />
    </>
  ),
};

function Offering() {
  useDocumentMeta(
    "Offering",
    "How we deliver Internal Developer Platforms — a four-layered model, an embedded product team, and principles that hold every time.",
  );

  return (
    <>
      <section className="page-hero">
        <div className="container">
          <p className="kicker">Offering</p>
          <h1 className="page-title">An IDP is more than a portal</h1>
          <p className="page-lead">
            An IDP is not yet another portal promising the moon and back. It's a
            developer platform that provides access to the organisation's
            sanctioned software supply chain — consistent, frictionless, and
            optimised for developer flow.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="kicker">How we work</p>
            <h2 className="section-title">Principles we bring every time</h2>
          </div>
          <div className="card-grid card-grid-3">
            {corePrinciples.map((p) => (
              <div className="principle-card" key={p.title}>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="section-head">
            <p className="kicker">How we start</p>
            <h2 className="section-title">Discover before building</h2>
            <p className="section-intro">
              A platform that doesn't remove real developer pain points is just
              shelfware. The only way to know what those pain points are is to
              go find them.
            </p>
          </div>
          <ol className="step-list">
            {discoverySteps.map((step, index) => (
              <li className="step" key={step.title}>
                <span className="step-num">{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="kicker">The product team</p>
            <h2 className="section-title">The team building the platform</h2>
            <p className="section-intro">
              A dedicated cross-functional team, with the expertise and
              experience from previous developer platform engagements
            </p>
          </div>
          <div className="card-grid card-grid-4">
            {teamRoles.map((role) => (
              <div className="role-card" key={role.role}>
                <span className="role-focus">{role.focus}</span>
                <h3>{role.role}</h3>
                <p>{role.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="section-head">
            <p className="kicker">The model</p>
            <h2 className="section-title">A four-layered platform</h2>
            <p className="section-intro">
              Separation of concerns, from developer intent to underlying
              infrastructure complexity. Each layer abstracts the one below it,
              so developers get a clean interface while the organisation keeps
              control of what runs underneath.
            </p>
          </div>
          <div className="layer-stack">
            {platformLayers.map((layer, index) => (
              <div className={`layer layer-${index}`} key={layer.name}>
                <svg
                  className="layer-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {LAYER_ICONS[layer.icon]}
                </svg>
                <div className="layer-text">
                  <span className="layer-name">{layer.name}</span>
                  <p className="layer-detail">{layer.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="kicker">Rollout</p>
            <h2 className="section-title">A phased rollout</h2>
            <p className="section-intro">
              Investing in a developer platform intiative, it's easy to lose
              focus, disappearing down yet another rabbit hole. We know what
              works, how the pieces fit together, and where to start, so value
              is created along the way.
            </p>
          </div>
          <div className="rollout">
            <div className="rollout-track">
              {rolloutPhases.map((phase, index) => (
                <div className="rollout-row" key={phase.name}>
                  <div
                    className={`rollout-bar rollout-${index}`}
                    style={{
                      marginInlineStart: `${
                        (phase.startMonth / rolloutTimelineMonths) * 100
                      }%`,
                    }}
                  >
                    <svg
                      className="rollout-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {ROLLOUT_ICONS[phase.icon]}
                    </svg>
                    <div className="rollout-text">
                      <p className="rollout-detail">
                        <span className="rollout-name">{phase.name}</span> -{" "}
                        {phase.detail}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="rollout-axis" aria-hidden="true">
              {rolloutMarkers.map((marker) => (
                <span
                  className="rollout-tick"
                  key={marker.label}
                  style={{
                    insetInlineStart: `${
                      (marker.month / rolloutTimelineMonths) * 100
                    }%`,
                  }}
                >
                  {marker.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container partner-note">
          <div className="section-head">
            <p className="kicker">Partnership with Red Hat</p>
            <h2 className="section-title">Built on the Red Hat ecosystem</h2>
          </div>
          <p className="section-intro">
            We work tightly with Red Hat to help their customers build Developer
            Platforms on Developer Hub and OpenShift — joint customer
            engagements, shared events, and continuous training so our
            consultants stay current as the platform evolves.
          </p>
          <div className="section-foot">
            <Link className="cta-button" to="/contact">
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default Offering;
